/**
 * Goal PROGRESS resolution — the pure function that gives every goal, of either
 * kind, the SAME trailing readout: a count and one meter.
 *
 * Before this, a strategic goal's trailing slot was a band word ("pending"), an
 * agentic goal's was a finish-line readout, and a goal with neither got the
 * text "No finish line". Three representations for one idea — how far along is
 * this? — which is exactly what made the two kinds look like different objects.
 *
 * Now there is one idea and one representation: a `value / max` ratio, drawn as
 * a single `<HealthBar>` in one color. Not a segmented track — a column of
 * segmented tracks reads as confetti, and the segment count carries no meaning
 * the number beside it doesn't already state.
 *
 * The ratio is sourced in priority order from whatever the goal actually has:
 *
 *   1. MILESTONES  — named checkpoints, the richest signal.
 *   2. FINISH LINE — the goal's own definition of done: a target metric, or a
 *      criteria-style tally. A strategic goal's key results arrive here (the
 *      server builds them into a `criteria` finish line), so KR attainment and
 *      an acceptance checklist read as the same bar.
 *   3. LINKED ISSUES — an agentic goal with no stated finish line still has a
 *      real one: the issues attached to it, each of which passed ship/verify.
 *      This data was already on the row and simply wasn't being drawn.
 *   4. NOTHING — the same bar, empty. A goal nothing is measuring looks like a
 *      goal that hasn't started, NOT like a different kind of object.
 *
 * Pure (no hooks, no imports beyond types) so it's safe in RSC and client trees.
 */

import type { GoalFinishLine } from "./goal-definition";
import type { GoalMilestone } from "./goal-milestones";

export interface GoalTrack {
  /** Filled amount and full scale — fed straight to `<HealthBar>`. */
  value: number;
  max: number;
  /** Rounded percentage, revealed on hover in place of the count. */
  pct: number;
  /** The exact tally, e.g. "12/18". Empty when nothing is measured. */
  count: string;
  /** Plain-language readout for the tooltip / screen readers. */
  caption: string;
  /** False when nothing measures this goal — the empty bar. */
  measured: boolean;
}

function track(
  value: number,
  max: number,
  count: string,
  caption: string,
): GoalTrack {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return { value, max, pct, count, caption, measured: true };
}

/** The fields `goalTrack` reads — a structural subset of `GoalLedgerItem`. */
export interface GoalTrackInput {
  kind: "agentic" | "strategic";
  milestones: GoalMilestone[];
  finishLine: GoalFinishLine;
  task_count: number;
  done_task_count: number;
  cancelled_task_count: number;
}

export function goalTrack(item: GoalTrackInput): GoalTrack {
  // 1. Named checkpoints — the richest signal.
  if (item.milestones.length > 0) {
    const done = item.milestones.filter((m) => m.status === "done").length;
    const total = item.milestones.length;
    return track(done, total, `${done}/${total}`, `${done} of ${total} milestones reached`);
  }

  // 2. The goal's own finish line.
  if (item.finishLine.kind === "metric") {
    const pct = Math.max(0, Math.min(100, item.finishLine.pct));
    return track(
      pct,
      100,
      `${pct}%`,
      `${item.finishLine.current} of ${item.finishLine.target}${
        item.finishLine.label ? ` — ${item.finishLine.label}` : ""
      }`,
    );
  }
  if (item.finishLine.kind === "criteria" && item.finishLine.total > 0) {
    const { met, total } = item.finishLine;
    const noun = item.finishLine.noun ?? "criteria";
    return track(met, total, `${met}/${total}`, `${met} of ${total} ${noun} met`);
  }

  // 3. Linked issues — an agentic goal's de-facto finish line. Cancelled work
  //    leaves the denominator: it was decided against, not left undone.
  const live = item.task_count - item.cancelled_task_count;
  if (live > 0) {
    return track(
      item.done_task_count,
      live,
      `${item.done_task_count}/${live}`,
      `${item.done_task_count} of ${live} linked issues verified done`,
    );
  }

  // 4. Nothing measures this goal yet — the same bar, empty.
  return {
    value: 0,
    max: 1,
    pct: 0,
    count: "",
    caption:
      item.kind === "strategic"
        ? "Nothing measuring this yet — add a key result"
        : "Nothing measuring this yet — link issues or set a finish line",
    measured: false,
  };
}
